import {Component, inject} from '@angular/core';
import {TrpcService} from '../../services/trpc/trpc.service';
import {LotSettings, SaveLotSettingsRequest} from '../../../../../express-tRPC/src/api/lot-settings/types';
// import { LotSettings } from "./types";

import { MessageService, ConfirmationService } from 'primeng/api';
import { FormBuilder, FormControl, Validators, FormGroup } from '@angular/forms';
import { dropdownOption } from '../../types/common';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { AccessCheckResult } from '../../../../../express-tRPC/src/invoice-library-shared/cross-domain-types';
import { CommonSharedService } from 'src/app/shared/services/common-shared.service';

// export interface AccessCheckResult {
//   result: 'allowed' | 'denied';
// }

const COMPONENT_FEATURE_NAME = 'SUREPARK Connection Settings';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  providers: [ConfirmationService]
})
export class SettingsComponent {

  version = environment.version;
  activeFile = environment.activeFile;

  // Custom validator to ensure only one field has a value
  mutuallyExclusiveValidator() {
    return (formGroup: FormGroup) => {
      const defaultLotNames = formGroup.get('default_lot_names')?.value;
      const apiLotId = formGroup.get('api_lotid')?.value;

      if ((defaultLotNames?.length > 0) && apiLotId) {
        return {mutuallyExclusive: true};
      }

      return null;
    };
  }

  // Custom validator for timezone mutual exclusivity
  timezoneExclusiveValidator() {
    return (formGroup: FormGroup) => {
      const timezoneGroup = formGroup.get('default_timezone_groups')?.value;
      const timezone = formGroup.get('api_timezoneid')?.value;

      if (timezoneGroup && timezone) {
        return {timezoneExclusive: true};
      }
      return null;
    };
  }

  loadingSettings: boolean = false;
  savingSettings: boolean = false;
  locationSettings!: LotSettings;

  // Store the location ID for access within the template
  locationId = '';

  private fb = inject(FormBuilder);

  // Flag controls whether the developer options are available for use in the UI. This indirectly activates the JSON
  // import / export such as the viewConfJSONDetails popup.
  devOptsEnabled: boolean = false;

  // Indicates whether the developer option to work with the JSON settings is open or closed.
  viewConfJSONDetails: boolean = false;
  importConfDialog: boolean = false;

  importedConfJSON: any = '';

  settingsForm = this.fb.group({
    wps_active: new FormControl<boolean>(false, [Validators.required]),
    parking_device: new FormControl<string>('', []),
    api_url: new FormControl<string>('', [Validators.required]),
    api_key: new FormControl<string>("", [Validators.required]),
    api_timezoneid: new FormControl<string>('', [Validators.required]),
    default_timezone_groups: new FormControl<string>('', [Validators.required]),
    default_lot_names: new FormControl<string[] | null>([], [Validators.required]),
    api_lotid: new FormControl<string | null>('', [Validators.required]),
    default_company_id: new FormControl<string>('', [Validators.required]),
    card_function_setting: new FormControl<string>('', [Validators.required]),
    group_passback_setting: new FormControl<string>('', [Validators.required]),
    set_apb_for_new_members: new FormControl<boolean>(false),
    hide_empty_surepark_groups: new FormControl<boolean>(false),
  }, {validators: [this.mutuallyExclusiveValidator(), this.timezoneExclusiveValidator()]});

  formSubmitted: boolean = false;

  timezoneOptions: dropdownOption[] = [];
  timezoneGroupOptions: dropdownOption[] = [];
  lotIdOptions: dropdownOption[] = [];
  groupIdOptions: dropdownOption[] = [];
  sureparlAllRateOptions: dropdownOption[] = [];
  filteredSureparlAllRates: any[] = [];
  antiPassbackOptions: dropdownOption[] = [
    {name: "None", code: "None"},
    {name: "Soft", code: "Soft"},
    {name: "Active", code: "Active"},
    {name: "Time", code: "Time"}
  ];

  antiPassHelpText: string = `<b>None</b> - Disables APB check.</br>
<b>Soft</b> - If triggered, customer will be allowed to enter/exit, but an event will be created.</br>
<b>Active</b> - If triggered, customer will not be allowed to enter/exit.</br>
<b>Time</b> - Active but will only check for violations from the same day.`;

  deviceParkingOptions: dropdownOption[] = [
    {name: "None", code: ""},
    {name: "Surepark", code: "surepark"},
  ];

  lotList: any[] = [];

  cardFunctionOptions: dropdownOption[] = [
    {name: "Monthly", code: "Monthly"},
    {name: "ValueCard", code: "ValueCard"}
  ];

  constructor(
    public route: ActivatedRoute,
    private trpcService: TrpcService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private titleService: Title,
    private commonSharedService: CommonSharedService
  ) {
    this.getSettings();
  }

  checkAccessToFeature() {
    this.trpcService.trpc.lots.checkAccessToFeature.mutate({
      feature: COMPONENT_FEATURE_NAME
    }).then((response: AccessCheckResult) => {

      if (response.result === 'denied') {
        this.messageService.add({
          severity: "error",
          detail: 'No access to ' + COMPONENT_FEATURE_NAME + ' feature',
          sticky: true
        });
      }

    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: 'Unable to check access to ' + COMPONENT_FEATURE_NAME + ' feature.',
        sticky: true
      });

    });
  }

  ngOnInit() {

    // Activate the developer options if supplied by URL
    let devOptions = this.route.snapshot.queryParamMap.get('dev-options');
    this.devOptsEnabled = devOptions == "true";

    this.trpcService.currentLocationObservable.subscribe((location: string) => {
      this.titleService.setTitle(`SUREPARK Config | ${location}`);
    });

    this.checkAccessToFeature();

  }

  backToPortal(): void {
    location.href = environment.SITE_URL_LOCAL;
  }

  async getSettings() {

    this.loadingSettings = true;

    let locationSettingsAny: any = await this.trpcService.trpc.settings.get.query().catch((error: any) => {
      this.loadingSettings = false;
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.locationSettings = locationSettingsAny as LotSettings;

    if (this.locationSettings) {

      this.timezoneOptions = this.locationSettings?.timezones?.map((timezone: any) => {
        return {code: timezone.id, name: timezone.name}
      })
      this.timezoneGroupOptions = this.locationSettings?.timezonegroups?.map((timezone: any) => {
        return {code: timezone.id, name: timezone.name}
      })
      this.lotIdOptions = this.locationSettings?.lots?.map((lot: any) => {
        return {code: lot.id, name: lot.name}
      })
      this.groupIdOptions = this.locationSettings?.groups?.map((group: any) => {
        return {code: group.id, name: group.name}
      })
      this.sureparlAllRateOptions = this.locationSettings?.sureParkAllrates?.map((rate: any) => {
        return {id: rate.id, name: rate.name}
      })

      this.lotList = this.locationSettings?.lots || [];
      let defaultLotIds: string[] = [];
      if (this.locationSettings.defaultLotIds?.length) {
        let selectedLotIds = this.lotList?.filter((lot: any) => this.locationSettings.defaultLotIds.includes(lot.name));
        defaultLotIds = selectedLotIds.map((lot: any) => lot.id);
      }

      this.locationId = this.locationSettings.locationID;

      if (this.locationSettings.apiLotId) {
        this.settingsForm.get('default_lot_names')?.clearValidators();
        this.settingsForm.get('default_lot_names')?.updateValueAndValidity();
      }

      if (defaultLotIds?.length) {
        this.settingsForm.get('api_lotid')?.clearValidators();
        this.settingsForm.get('api_lotid')?.updateValueAndValidity();
      }

      //Set Value in drop down after options are assigned so value will get selected otherwise value wont get selected
      setTimeout(() => {
        this.settingsForm.setValue({
          wps_active: Boolean(this.locationSettings.wpsActive || 0),
          parking_device: this.locationSettings.parkingDevice,
          api_url: this.locationSettings.apiURL,
          api_key: this.locationSettings.apiKey,
          api_timezoneid: this.locationSettings.apiTimezonId,
          default_timezone_groups: this.locationSettings.timezoneGroupId,
          default_lot_names: defaultLotIds,
          api_lotid: this.locationSettings.apiLotId,
          default_company_id: this.locationSettings.defaultCompanyId,
          card_function_setting: this.locationSettings.cardFunctionSetting,
          group_passback_setting: this.locationSettings.groupPassbackSetting,
          set_apb_for_new_members: Boolean(this.locationSettings.setApbForNewMembers || false),
          hide_empty_surepark_groups: Boolean(this.locationSettings.hideEmptySureparkGroups || false)
        });
      }, 0);

      if (!this.locationSettings.apiURL && !this.locationSettings.apiKey) {

        this.settingsForm.get('api_timezoneid')?.setValidators([]);
        this.settingsForm.get('default_timezone_groups')?.setValidators([]);
        this.settingsForm.get('default_lot_names')?.setValidators([]);
        this.settingsForm.get('api_lotid')?.setValidators([]);
        this.settingsForm.get('default_company_id')?.setValidators([]);
        this.settingsForm.get('card_function_setting')?.setValidators([]);
        this.settingsForm.get('group_passback_setting')?.setValidators([]);
        this.settingsForm.get('set_apb_for_new_members')?.setValidators([]);

      } else {

        this.settingsForm.get('api_timezoneid')?.setValidators([Validators.required]);
        this.settingsForm.get('default_timezone_groups')?.setValidators([Validators.required]);
        this.settingsForm.get('default_lot_names')?.setValidators([Validators.required]);
        this.settingsForm.get('api_lotid')?.setValidators([Validators.required]);
        this.settingsForm.get('default_company_id')?.setValidators([Validators.required]);
        this.settingsForm.get('card_function_setting')?.setValidators([Validators.required]);
        this.settingsForm.get('group_passback_setting')?.setValidators([Validators.required]);
        this.settingsForm.get('set_apb_for_new_members')?.setValidators([]);

      }

      setTimeout(() => {
        //Update Dropdown valiations
        this.defaultGroupStationsChanged({value: this.settingsForm.get('default_lot_names')?.value} as DropdownChangeEvent); //default_lot_names
        this.defaultGroupLotChanged({value: this.settingsForm.get('api_lotid')?.value} as DropdownChangeEvent); //api_lotid
        this.defaultTimezoneGroupChanged({value: this.settingsForm.get('default_timezone_groups')?.value} as DropdownChangeEvent); //default_timezone_groups
        this.defaultTimezoneChanged({value: this.settingsForm.get('api_timezoneid')?.value} as DropdownChangeEvent); //api_timezoneid
      }, 0);

    }
    // console.log(`Location settings: `, locationSettingsAny);

    console.log(`Location settings: `, this.locationSettings);

    this.loadingSettings = false;

  }

  filterDefaultRates(event: AutoCompleteCompleteEvent) {
    let query = event.query;

    this.filteredSureparlAllRates = this.sureparlAllRateOptions?.filter((rate: any) => {
      return (rate.name.toLowerCase().indexOf(query.toLowerCase()) != -1 || String(rate.code).toLowerCase().indexOf(String(query)) != -1)
    });
  }

  parkingDeviceChanged(event: DropdownChangeEvent): void {
    this.updateSetting('parking_device', event.value);
  }

  defaultGroupStationsChanged(event: DropdownChangeEvent): void {
    console.log("defaultGroupStationsChanged", event)
    if (event.value.length) {
      this.settingsForm.get('api_lotid')?.reset();
      this.settingsForm.get('api_lotid')?.clearValidators();
    } else {
      this.settingsForm.get('api_lotid')?.setValidators([Validators.required]);
    }
    this.settingsForm.get('api_lotid')?.updateValueAndValidity()
  }

  defaultGroupLotChanged(event: DropdownChangeEvent): void {
    console.log("defaultGroupLotChanged", event);

    if (event.value) {
      this.settingsForm.get('default_lot_names')?.reset();
      this.settingsForm.get('default_lot_names')?.clearValidators();
    } else {
      this.settingsForm.get('default_lot_names')?.setValidators([Validators.required]);
    }
    this.settingsForm.get('default_lot_names')?.updateValueAndValidity()
  }

  defaultTimezoneGroupChanged(event: DropdownChangeEvent): void {
    if (event.value) {
      this.settingsForm.get('api_timezoneid')?.reset();
      this.settingsForm.get('api_timezoneid')?.clearValidators();
    } else {
      this.settingsForm.get('api_timezoneid')?.setValidators([Validators.required]);
    }
    this.settingsForm.get('api_timezoneid')?.updateValueAndValidity()
  }

  defaultTimezoneChanged(event: DropdownChangeEvent): void {
    if (event.value) {
      this.settingsForm.get('default_timezone_groups')?.reset();
      this.settingsForm.get('default_timezone_groups')?.clearValidators();
    } else {
      this.settingsForm.get('default_timezone_groups')?.setValidators([Validators.required]);
    }
    this.settingsForm.get('default_timezone_groups')?.updateValueAndValidity()
  }

  /**
   * Occurs when the device settings are activated. This is mean to initially save the API Key and URL
   * so that the form can release and the dependant fields can be activated. It should bypass the field validations
   * because the form will be invalid on first same since none of the required fields were loaded with options
   */
  async saveSettingsWhenActivated() {
    // Clear out any existing form errors and save whatever we have loaded
    this.settingsForm.setErrors(null);
    this.saveSettings(this.settingsForm.value);
  }

  async saveSettings(suppliedSettings: any = undefined) {

    this.messageService.clear();

    this.formSubmitted = true;

    if (this.settingsForm.invalid && !suppliedSettings) {
      if (this.settingsForm.errors?.['mutuallyExclusive']) {
        this.messageService.add({
          severity: 'error',
          detail: 'Please select either Default Group Stations OR Default Group Lot, not both.'
        });
      } else if (this.settingsForm.errors?.['timezoneExclusive']) {
        this.messageService.add({
          severity: 'error',
          detail: 'Please select either Default Timezone Group OR Default Timezone, not both.'
        });
      }
      return;
    }

    this.formSubmitted = false;

    this.savingSettings = true;
    const formValues = suppliedSettings || this.settingsForm.value;
    let default_stations: string[] = [];
    let default_lot_names: string[] = [];
    // console.log(formValues);
    if (formValues['default_lot_names']?.length) {
      let matched_lots = this.lotList.filter((lot: any) => formValues?.['default_lot_names']?.includes(lot.id));
      matched_lots.forEach((lot: any) => {
        default_lot_names.push(lot.name);
        let uniqueLotStations = lot.lotStations.filter((lotStation: any) => !default_stations.includes(lotStation.stationId))
        let stationIds: string[] = uniqueLotStations.map((lotStation: any) => lotStation.stationId);
        default_stations = default_stations.concat(stationIds);
      })
    }

    let values: SaveLotSettingsRequest = {
      wps_active: formValues['wps_active'] || false,
      parking_device: formValues['parking_device'],
      api_url: this.commonSharedService.cleanUrl(formValues['api_url'] || ''),
      api_key: formValues['api_key'] || '',
      api_timezoneid: formValues['api_timezoneid'] || '',
      default_timezone_groups: formValues['default_timezone_groups'] || '',
      default_lot_names: default_lot_names,
      api_lotid: formValues['api_lotid'] || '',
      default_company_id: formValues['default_company_id'] || '',
      default_stations: default_stations,
      card_function_setting: formValues['card_function_setting'] || '',
      group_passback_setting: formValues['group_passback_setting'] || '',
      set_apb_for_new_members: formValues['set_apb_for_new_members'] || false,
      hide_empty_surepark_groups: formValues['hide_empty_surepark_groups'] || false
    };

    await this.trpcService.trpc
      .settings
      .set
      .mutate({
        settings: values
      }).then(() => {
        this.messageService.add({
          severity: "success",
          detail: "Lot Settings saved successfully.",
        });

        this.savingSettings = false;
        this.getSettings();
      }).catch((error: any) => {
        this.savingSettings = false;
        this.messageService.add({
          severity: "error",
          detail: error.message,
        });
      });
  }

  /**
   * This function will create/update specific setting
   * @param settingName string
   * @param settingValue string
   */
  async updateSetting(settingName: string, settingValue: string): Promise<void> {
    await this.trpcService.trpc.settings.update.mutate({settingName: settingName, settingValue: settingValue});
  }

  /**
   * This function will copy JSON to clipboard
   */
  copyJSON() {
    let settingsToCopy: any = Object.assign({}, this.settingsForm.value);
    settingsToCopy['lotList'] = this.lotList;

    navigator.clipboard.writeText(JSON.stringify(settingsToCopy, undefined, 2));
    this.messageService.add({
      severity: "success",
      detail: "Configurations copied!",
    });
  }

  /**
   * This function will get configuration JSON from textarea and save it
   */
  async importJSON() {
    try {
      let settings = JSON.parse(this.importedConfJSON);
      this.lotList = settings?.lotList || [];
      await this.saveSettings(settings);
      this.importConfDialog = false;
    } catch (error: any) {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    }
  }

  /**
   * This is meant to be a developer options for troubleshooting to completely reset the connection
   * to SUREPARK so it can be reconfigured.
   */
  async clearConfiguration() {

    let values: SaveLotSettingsRequest = {
      wps_active: false,
      parking_device: '',
      api_url: '',
      api_key: '',
      api_timezoneid: '',
      default_timezone_groups: '',
      default_lot_names: [],
      api_lotid: '',
      default_company_id: '',
      default_stations: [],
      card_function_setting: '',
      group_passback_setting: '',
      set_apb_for_new_members: false,
      hide_empty_surepark_groups: false
    };

    await this.trpcService.trpc
      .settings
      .set
      .mutate({
        settings: values
      }).then(() => {
        this.messageService.add({
          severity: "success",
          detail: "Lot Settings saved successfully.",
        });

        this.savingSettings = false;
        this.getSettings();
      }).catch((error: any) => {
        this.savingSettings = false;
        this.messageService.add({
          severity: "error",
          detail: error.message,
        });
      });
  }

  confirmClearConfiguration() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to clear the configuration? This action cannot be undone.',
      header: 'Confirm Clearance',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.clearConfiguration();
      },
      reject: () => {
        // Optional: Handle rejection, e.g., show a message or do nothing
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelled',
          detail: 'Configuration clearing was cancelled.'
        });
      }
    });
  }

}
